import {
  Autocomplete,
  Box,
  Chip,
  TextField,
} from '@mui/material';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { getOptionValue } from '@lib/form';
import FieldLabel from '../../FieldLabel';
import { FIELD_HEIGHT } from '../../formTheme';
import getFieldStatus from '../../fieldStatus';

const TRANSLATION_PREFIX = 'components.molecules.form';

/**
 * A select you type into.
 *
 * Once an option list runs past a screenful — manufacturers, models, locations — scanning a
 * drop-down costs more than typing three letters. Built on MUI's `Autocomplete` rather than
 * by hand so the filtering, keyboard handling and combobox semantics come for free, and so
 * the field inherits the form theme like every other control.
 *
 * Selected values are chips inside the field, which is what makes the multi-select case
 * readable: a comma-joined string in a 48px box truncates after about three answers.
 */
export default function Searchable(props) {
  const {
    name,
    disabled,
    value,
    label = '',
    isRequired,
    error,
    touched,
    helperText = '',
    isMultiple,
    options = [],
    handleChange,
    handleBlur,
    readOnly,
  } = props;
  const { t } = useTranslation();
  const { hasError, message } = getFieldStatus({ error, touched, helperText });

  const findOption = (optionValue) => options
    .find((o) => getOptionValue(o) === optionValue) ?? null;

  // Guarded the way `DropDown` guards it: a multi-select whose Formik value is briefly a
  // string would otherwise throw on `.map`.
  const selected = isMultiple
    ? (Array.isArray(value) ? value : []).map(findOption).filter(Boolean)
    : findOption(value);

  return (
    <Autocomplete
      disableCloseOnSelect={isMultiple}
      disabled={disabled}
      getOptionKey={getOptionValue}
      getOptionLabel={(option) => option.label ?? ''}
      isOptionEqualToValue={
        (option, selectedOption) => getOptionValue(option) === getOptionValue(selectedOption)
      }
      multiple={isMultiple}
      noOptionsText={t(`${TRANSLATION_PREFIX}.noOptions`)}
      onBlur={handleBlur}
      onChange={(event, newValue) => handleChange(toFieldValue(newValue, isMultiple))}
      options={options}
      popupIcon={<FiChevronDown />}
      readOnly={readOnly}
      renderInput={(params) => (
        <TextField
          {...params}
          error={hasError}
          helperText={message}
          inputProps={{ ...params.inputProps, 'aria-required': isRequired || undefined }}
          label={<FieldLabel isRequired={isRequired} label={label} />}
          name={name}
          placeholder={selected?.length === 0 || !selected
            ? t(`${TRANSLATION_PREFIX}.searchPlaceholder`)
            : undefined}
        />
      )}
      renderOption={(optionProps, option, { selected: isSelected }) => {
        const { key, ...rest } = optionProps;

        return (
          <Box
            key={key}
            {...rest}
            component="li"
            sx={{
              fontWeight: isSelected ? 700 : 400,
              justifyContent: 'space-between',
            }}
          >
            {option.label}
            {isSelected && (
              <Box aria-hidden sx={{ display: 'flex', color: 'primary.main' }}>
                <FiCheck size={16} strokeWidth={3} />
              </Box>
            )}
          </Box>
        );
      }}
      renderTags={(selectedOptions, getTagProps) => selectedOptions.map((option, index) => {
        const { key, ...chipProps } = getTagProps({ index });

        return (
          // `Chip` is already a pill with the right height and weight; only the fill is
          // ours, so the design's grey chip reads as a value rather than an action.
          <Chip
            key={key}
            label={option.label}
            {...chipProps}
            sx={{ backgroundColor: 'quaternary.main', fontWeight: 600 }}
          />
        );
      })}
      sx={{
        // The control grows with its chips, so the fixed height from the form theme has to
        // become a floor rather than a height.
        '& .MuiOutlinedInput-root': {
          height: 'auto',
          minHeight: FIELD_HEIGHT,
          py: 0.75,
          gap: 1,
        },
      }}
      value={isMultiple ? selected : (selected ?? null)}
    />
  );
}

/** Autocomplete hands back option objects; Formik stores the plain values. */
function toFieldValue(newValue, isMultiple) {
  if (isMultiple) {
    return (newValue ?? []).map(getOptionValue);
  }

  return newValue ? getOptionValue(newValue) : '';
}

Searchable.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  error: PropTypes.string,
  // Formik reports an array-valued field's touched state as an array of booleans,
  // not a boolean.
  touched: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  helperText: PropTypes.string,
  isMultiple: PropTypes.bool,
  options: PropTypes.array,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};
